import { ProcessEvent } from '../types'

/** Linha da janela de debug: uma etapa (início + fim casados), um progresso ou uma anotação. */
export interface StepRow {
  key: string
  kind: 'step' | 'progress' | 'log'
  label: string
  /** ms desde o início do processamento. */
  offsetMs: number
  state: 'running' | 'done' | 'error' | 'info'
  durationMs?: number
  startedAtMs?: number
  error?: string
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)} s`
  const m = Math.floor(s / 60)
  return `${m} min ${String(Math.floor(s % 60)).padStart(2, '0')} s`
}

/** Deslocamento desde o início, compacto para a coluna de tempo: `+2:41`. */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h > 0 ? `+${h}:${String(m).padStart(2, '0')}:${s}` : `+${m}:${s}`
}

/**
 * Junta início e fim de cada etapa numa linha só. Etapas com o mesmo nome
 * (uma por live, por exemplo) casam em ordem: o fim fecha o início aberto mais
 * antigo daquele nome.
 */
export function buildRows(events: ProcessEvent[], startedAtMs: number): StepRow[] {
  const rows: StepRow[] = []
  const open = new Map<string, StepRow[]>()
  for (const e of events) {
    const atMs = Date.parse(e.at)
    const offsetMs = atMs - startedAtMs
    if (e.kind === 'step-start') {
      const row: StepRow = { key: `s${e.seq}`, kind: 'step', label: e.label, offsetMs, state: 'running', startedAtMs: atMs }
      rows.push(row)
      open.set(e.label, [...(open.get(e.label) ?? []), row])
    } else if (e.kind === 'step-end' || e.kind === 'step-error') {
      const pending = open.get(e.label)
      const row = pending?.shift()
      if (row) {
        row.state = e.kind === 'step-end' ? 'done' : 'error'
        row.durationMs = e.durationMs
        row.error = e.error
      } else {
        // Erro geral do processamento (sem etapa aberta com esse nome).
        rows.push({
          key: `e${e.seq}`,
          kind: 'log',
          label: e.label,
          offsetMs,
          state: e.kind === 'step-error' ? 'error' : 'info',
          durationMs: e.durationMs,
          error: e.error,
        })
      }
    } else {
      rows.push({ key: `p${e.seq}`, kind: e.kind === 'progress' ? 'progress' : 'log', label: e.label, offsetMs, state: 'info' })
    }
  }
  return rows
}
